import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEditor, EditorContent } from "@tiptap/react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Star, Trash2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { baseExtensions } from "@/components/rich/extensions";
import { FootnoteMarks } from "@/components/studio/pagination";
import { coverUrl } from "@/components/studio/BookDetails";
import { api, errMsg } from "@/lib/api";
import { chapterLabel, fmtMinutes, readMinutes } from "@/lib/studio";
import { FanBadge } from "@/components/fanworks/FanBadge";
import { RightsReportButton } from "@/components/fanworks/RightsReportButton";

export const ReaderText = ({ md }) => {
  const editor = useEditor({ extensions: [...baseExtensions({}), FootnoteMarks], content: md, editable: false, editorProps: { attributes: { class: "lf-prose lf-paper lf-reader", "data-testid": "reader-text" } } });
  useEffect(() => { if (editor && !editor.isDestroyed) editor.commands.setContent(md || "", false); }, [editor, md]);
  return <EditorContent editor={editor} />;
};

const Stars = ({ value, onRate, disabled }) => (
  <div className="flex gap-0.5" data-testid="reader-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} disabled={disabled} onClick={() => onRate(n)} className="disabled:cursor-default" data-testid={`reader-star-${n}`}>
        <Star className={`h-5 w-5 ${n <= (value || 0) ? "fill-[#FBBF24] text-[#FBBF24]" : "text-[#A8A090]"}`} />
      </button>
    ))}
  </div>
);

const Comments = ({ sid, cid, enabled }) => {
  const qc = useQueryClient();
  const key = ["reader-comments", sid, cid];
  const { data = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/read/${sid}/chapters/${cid}/comments`).then((r) => r.data) });
  const [text, setText] = useState("");
  const post = async () => { try { await api.post(`/read/${sid}/chapters/${cid}/comments`, { text }); setText(""); qc.invalidateQueries({ queryKey: key }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="mx-auto mt-12 max-w-2xl" data-testid="reader-comments">
      <div className="lf-label mb-3">Comments on this chapter ({data.length})</div>
      <div className="space-y-3">
        {data.map((c) => (
          <div key={c.id} className="rounded-md border border-[#2D3442] bg-[#13171F] p-3" data-testid={`reader-comment-${c.id}`}>
            <div className="flex justify-between text-xs text-[#A8A090]"><span><b className="text-[#F3EBDD]">{c.user_name}</b> · {new Date(c.created_at).toLocaleDateString()}</span>
              {c.can_delete && <button onClick={() => api.delete(`/story-comments/${c.id}`).then(() => qc.invalidateQueries({ queryKey: key }))} className="hover:text-[#F87171]" data-testid={`reader-comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[#F3EBDD]">{c.text}</p>
          </div>
        ))}
      </div>
      {enabled ? <div className="mt-4"><Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share your thoughts with the author…" data-testid="reader-comment-input" /><Button size="sm" className="mt-2" disabled={!text.trim()} onClick={post} data-testid="reader-comment-post">Post comment</Button></div>
        : <p className="mt-4 text-xs text-[#A8A090]" data-testid="reader-feedback-off">The author has turned off comments.</p>}
    </section>
  );
};

const scrollFrac = () => { const max = document.documentElement.scrollHeight - window.innerHeight; return max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0; };

const useReadingProgress = (sid, ch, prog, ready, track) => {
  const restored = useRef(false);
  useEffect(() => {
    if (!ch || !ready) return;
    const resume = !restored.current && prog?.chapter_id === ch.id && prog.scroll > 0;
    restored.current = true;
    const t = setTimeout(() => window.scrollTo(0, resume ? prog.scroll * (document.documentElement.scrollHeight - window.innerHeight) : 0), resume ? 250 : 0);
    if (!track) return () => clearTimeout(t);
    const save = (scroll = scrollFrac()) => api.put(`/read/${sid}/progress`, { chapter_id: ch.id, scroll }).catch(() => {});
    if (!resume) save(0);
    let timer;
    const onScroll = () => { clearTimeout(timer); timer = setTimeout(() => save(), 1200); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, [sid, ch?.id, ready, track]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default function ReaderPage() {
  const { sid } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [toc, setToc] = useState(false);
  const { data: s, error } = useQuery({ queryKey: ["reader", sid], queryFn: () => api.get(`/read/${sid}`).then((r) => r.data), retry: false });
  const { data: prog, isFetched: progReady } = useQuery({ queryKey: ["reader-progress", sid], queryFn: () => api.get(`/read/${sid}/progress`).then((r) => r.data), retry: false, refetchOnWindowFocus: false });
  const cid = params.get("c") || (progReady ? prog?.chapter_id || s?.chapters?.[0]?.id : null);
  const { data: ch } = useQuery({ queryKey: ["reader-chapter", sid, cid], queryFn: () => api.get(`/read/${sid}/chapters/${cid}`).then((r) => r.data), enabled: !!cid && !!s });
  useReadingProgress(sid, ch, prog, progReady, s && s.role !== "owner");
  if (error) return <div className="p-10 text-sm text-[#F87171]" data-testid="reader-error">{errMsg(error)}</div>;
  if (!s) return null;
  const i = s.chapters.findIndex((c) => c.id === cid);
  const go = (c) => { setToc(false); setParams({ c: c.id }); };
  const rate = async (n) => { try { await api.put(`/read/${sid}/rating`, { stars: n }); qc.invalidateQueries({ queryKey: ["reader", sid] }); toast.success("Thanks for rating"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="min-h-screen bg-[#0B0D10] lg:grid lg:grid-cols-[300px_1fr]" data-testid="reader-page">
      <aside className="border-b border-[#2D3442] bg-[#0F131A] p-6 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:border-b-0 lg:border-r">
        <Link to={s.role === "owner" || s.role === "member" ? `/u/${s.universe_id}/studio/${sid}` : "/library"} className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="reader-back"><ChevronLeft className="h-3.5 w-3.5" />{s.role === "reader" ? "More Universes" : "Back"}</Link>
        <div className="mt-5 flex gap-3">
          <div className="flex aspect-[2/3] w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-[#1A202C]">{s.has_cover ? <img src={coverUrl(s)} alt="" className="h-full w-full object-cover" data-testid="reader-cover" /> : <BookOpen className="h-5 w-5 text-[#A8A090]" />}</div>
          <div className="min-w-0"><div className="font-display text-2xl leading-tight text-[#F3EBDD] [overflow-wrap:anywhere]" data-testid="reader-title">{s.title}</div>{s.author && <div className="text-xs text-[#A8A090]">by {s.author}</div>}<div className="text-[10px] uppercase tracking-[0.14em] text-[#A8A090]">{s.universe_name}</div></div>
        </div>
        <FanBadge story={s} className="mt-3" />
        <div className="mt-4 flex items-center gap-2">{s.feedback_enabled && s.role !== "owner" ? <Stars value={s.my_rating} onRate={rate} /> : <Stars value={Math.round(s.rating || 0)} disabled />}<span className="text-xs text-[#A8A090]" data-testid="reader-rating">{s.rating ? `${s.rating} · ${s.ratings} rating${s.ratings === 1 ? "" : "s"}` : "No ratings yet"}</span></div>
        <button onClick={() => setToc((v) => !v)} className="mt-6 flex w-full items-center justify-between rounded-md border border-[#2D3442] px-3 py-2 text-sm text-[#D6CCBC] lg:hidden" data-testid="reader-toc-toggle">
          <span>{i >= 0 ? chapterLabel(i + 1, s.chapters[i].title) : "Chapters"}</span><span className="text-xs text-[#A8A090]">{toc ? "Hide" : `All ${s.chapters.length}`}</span>
        </button>
        <nav className={`mt-3 space-y-1 lg:mt-6 lg:block ${toc ? "block" : "hidden"}`} data-testid="reader-toc">
          {s.chapters.map((c, n) => (
            <button key={c.id} onClick={() => go(c)} className={`block w-full rounded px-3 py-2 text-left text-sm ${c.id === cid ? "bg-[#1A202C] text-[#F59E0B]" : "text-[#D6CCBC] hover:bg-[#13171F]"}`} data-testid={`reader-toc-${c.id}`}>
              {chapterLabel(n + 1, c.title)}<span className="block text-[11px] text-[#A8A090]">{fmtMinutes(readMinutes(c.word_count, s.wpm))} read</span>
            </button>
          ))}
        </nav>
        {s.role !== "owner" && s.sharing !== "off" && <div className="mt-8 border-t border-[#2D3442] pt-4"><RightsReportButton sid={sid} title={s.title} /></div>}
      </aside>
      <main className="px-5 py-12 sm:px-10">
        {ch && <>
          <article className="mx-auto max-w-2xl rounded-sm bg-[#F6F0E4] px-6 py-12 shadow-[0_10px_40px_rgba(0,0,0,0.5)] sm:px-14" data-testid="reader-article">
            <h1 className="mb-8 text-center font-display text-3xl text-[#231D15] sm:text-4xl" data-testid="reader-chapter-title">{chapterLabel(ch.number, ch.title)}</h1>
            <ReaderText md={ch.content} />
          </article>
          <div className="mx-auto mt-6 flex max-w-2xl justify-between">
            <Button variant="ghost" disabled={i <= 0} onClick={() => go(s.chapters[i - 1])} data-testid="reader-prev"><ChevronLeft className="mr-1 h-4 w-4" />Previous</Button>
            <Button variant="ghost" disabled={i >= s.chapters.length - 1} onClick={() => go(s.chapters[i + 1])} data-testid="reader-next">Next<ChevronRight className="ml-1 h-4 w-4" /></Button>
          </div>
          <Comments sid={sid} cid={cid} enabled={s.feedback_enabled} />
        </>}
        {s.chapters.length === 0 && <p className="text-center text-sm text-[#A8A090]">No chapters yet.</p>}
      </main>
    </div>
  );
}
